import { FC } from 'react';

import { useFormContext } from 'react-hook-form';

import { z } from 'zod';

import Checkbox from '../../../../components/Checkbox/Checkbox';
import Input from '../../../../components/Input/Input';
import RichText from '../../../../components/RichText/RichText';
import Switch from '../../../../components/Switch/Switch';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '../../../../components/ui';
import { pageFormSchema } from '../validation-schema';

type PageFormType = z.infer<typeof pageFormSchema>;

export const StoreRedirectSection: FC = () => {
  const {
    control,
    getFieldState,
    watch,
    formState: { errors }
  } = useFormContext<PageFormType>();

  const isEnabled = watch('redirectConfig.enabled') ?? false;

  const getFieldError = (fieldName: string) => {
    const fieldState = getFieldState(fieldName as any);
    return fieldState.error;
  };

  return (
    <div
      className="mt-9 pt-9 mb-9 pb-9 border-t border-b-[1px] border-base-sidebarBorder"
      data-testid="PagesForm-RedirectSection"
    >
      <div className="flex items-start justify-between gap-4">
        <RichText
          title="Redirect URL"
          description="Configure CTA behavior and redirects."
          dataTestId="PagesForm-RedirectSection-Header"
        />
        <FormField
          control={control}
          name="redirectConfig.enabled"
          render={({ field }) => (
            <Switch
              key={field.value ? 'on' : 'off'}
              defaultChecked={field.value}
              checked={field.value}
              onCheckedChange={(checked) => {
                field.onChange(checked);
              }}
              data-testid="PagesForm-Switch-Redirect"
            />
          )}
        />
      </div>
      {isEnabled && (
        <div className="mt-5">
          <FormField
            control={control}
            name="redirectConfig.redirectUrl"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    isRequired
                    label="URL"
                    placeholder="https://example.com"
                    description={errors.redirectConfig?.redirectUrl?.message}
                    {...field}
                    value={field.value ?? ''}
                    className="mb-5"
                    state={
                      getFieldError(`redirectConfig.redirectUrl`)?.message
                        ? 'error'
                        : 'default'
                    }
                    data-testid="PagesForm-Input-RedirectUrl"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="redirectConfig.isStoreRedirect"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    label="Redirect to Web Store"
                    dataTestId="PagesForm-Checkbox-StoreRedirect"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};
